import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { MazCopilotActions } from '@ninety/web/pages/maz/_state/maz-copilot.actions';
import { MazCopilotLayout, mazCopilotLayoutFor } from '@ninety/web/pages/maz/models/maz-copilot.model';

/**
 * web-maz-redesign: whether Maz fits beside the page depends on the nav's width (full, icon rail or hidden) as well as
 * the window's, so this measures the layout rather than matching a media query.
 */
@Directive({
  selector: '[ninetyMazCopilotLayout]',
  standalone: true,
})
export class MazCopilotLayoutDirective implements OnChanges, OnDestroy {
  private store = inject(Store);
  private zone = inject(NgZone);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input({ alias: 'ninetyMazCopilotLayout', required: true }) enabled: boolean | null = false;

  private observer?: ResizeObserver;
  private layout: MazCopilotLayout | null = null;

  ngOnChanges(): void {
    if (!this.enabled) return this.disconnect();
    if (this.observer) return;
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private measure(): void {
    const nav = this.nav();
    // Folding the nav doesn't resize the layout, so watch the nav too (observing twice is a no-op).
    nav.forEach(element => this.observer?.observe(element));
    const navWidth = nav.reduce((width, element) => width + element.offsetWidth, 0);
    const layout = mazCopilotLayoutFor(this.host.nativeElement.clientWidth, navWidth);
    if (layout === this.layout) return;
    this.layout = layout;
    this.zone.run(() => this.store.dispatch(MazCopilotActions.layoutChanged({ layout })));
  }

  // Whatever the page-sidebar slot projected: everything before the page.
  private nav(): HTMLElement[] {
    const children = Array.from(this.host.nativeElement.children) as HTMLElement[];
    const page = children.findIndex(child => child.id === 'main-page-content');
    return children.slice(0, Math.max(page, 0));
  }

  private disconnect(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.layout = null;
  }
}
